import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import { apiList } from "@/content/site";
import { apiCatalogue, DOC_TYPES } from "@/content/apiCatalogue";
import usePageMeta from "@/hooks/usePageMeta";
import { PageHero } from "@/components/sections/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { FilterTabs } from "@/components/common/FilterTabs";
import { ApiRow } from "@/components/sections/ApiRow";

const FILTERS = [{ id: "all", label: "All" }, ...DOC_TYPES.map((d) => ({ id: d, label: d }))];

const matches = (item, q) => !q || item.name.toLowerCase().includes(q) || item.category.toLowerCase().includes(q);

export default function ApiList() {
  usePageMeta(apiList.meta);
  const [query, setQuery] = useState("");
  const [doc, setDoc] = useState("all");
  const q = query.trim().toLowerCase();

  const searched = useMemo(() => apiCatalogue.filter((i) => matches(i, q)), [q]);
  const shown = doc === "all" ? searched : searched.filter((i) => i.docs.includes(doc));
  const countFor = (id) => (id === "all" ? searched.length : searched.filter((i) => i.docs.includes(id)).length);

  return (
    <>
      <PageHero hero={apiList.hero} testId="api-list-hero" />

      <section className="pb-24 sm:pb-32" data-testid="api-list-section">
        <div className="container-x">
          <div className="flex flex-col gap-5 border-y border-line py-5 lg:flex-row lg:items-center lg:justify-between">
            <label className="relative block w-full lg:max-w-md">
              <span className="sr-only">Search products</span>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by product or therapeutic area"
                className="field-input pl-11"
                data-testid="api-list-search-input"
              />
            </label>
            <FilterTabs filters={FILTERS} active={doc} onSelect={setDoc} countFor={countFor} label="Filter by regulatory document" testIdPrefix="api-list" />
          </div>

          <p className="mt-5 font-mono text-[12px] uppercase tracking-[0.12em] text-muted" data-testid="api-list-count">
            Showing {shown.length} of {apiCatalogue.length} products
          </p>

          <div className="mt-6 hidden grid-cols-[56px_1.3fr_1fr_240px_150px] gap-4 border-b border-ink/80 bg-paper pb-3 font-mono text-[12px] uppercase tracking-[0.12em] text-muted lg:grid" aria-hidden="true">
            <span>No.</span>
            <span>Product</span>
            <span>Therapeutic area</span>
            <span>Documents</span>
            <span className="text-right">Quote</span>
          </div>

          {shown.length ? (
            <ul className="divide-y divide-line border-b border-line bg-paper" data-testid="api-list-results">
              {shown.map((item, i) => (
                <ApiRow key={item.name} item={item} index={i} />
              ))}
            </ul>
          ) : (
            <div className="border border-line bg-surface p-8 sm:p-10" data-testid="api-list-empty">
              <p className="font-display text-2xl font-semibold tracking-[-0.025em] text-ink">No products match your search</p>
              <p className="mt-3 text-[15px] text-muted">SPC sources beyond this list. Tell us what you need and we will check availability.</p>
              <Link to="/sourcing#enquiry" className="mt-6 inline-flex items-center gap-1 text-[15px] font-semibold text-accent underline-offset-4 hover:underline" data-testid="api-list-empty-enquiry-link">
                Send a sourcing enquiry <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          <p className="mt-8 max-w-3xl text-[14px] leading-relaxed text-muted" data-testid="api-list-note">{apiList.note}</p>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
